import { Injectable } from '@angular/core';
import { EventScriptRuntimeService } from './event-script-runtime.service';
import { ScriptValidatorService } from './script-validator.service';
import { EventConfig, EventContext } from '../interfaces/event-script.models';
import { BuilderNode } from '../interfaces/builder-screen.models';

/**
 * Serviço orquestrador principal para event scripting
 * Gerencia execução de eventos onInit e onChange
 */
@Injectable({ providedIn: 'root' })
export class EventScriptService {

  constructor(
    private runtimeService: EventScriptRuntimeService,
    private validatorService: ScriptValidatorService
  ) {}

  /**
   * Executa eventos onInit de um componente
   * Chamado quando o componente é montado/inicializado
   * @param node Node do componente
   * @param formData Dados atuais do formulário
   */
  executeInitEvents(node: BuilderNode, formData: Record<string, any>): void {
    const events = this.getEventsForType(node, 'onInit');
    
    if (events.length === 0) {
      return;
    }

    console.log(`[EventScript] Executando ${events.length} evento(s) onInit para componente:`, node.id);

    events.forEach(event => {
      const context = this.buildContext(node, formData, null, null);
      this.runtimeService.execute(event, context);
    });
  }

  /**
   * Executa eventos onChange de um componente
   * Chamado quando o valor do componente é alterado
   * @param node Node do componente
   * @param newValue Novo valor
   * @param oldValue Valor anterior
   * @param formData Dados atuais do formulário
   */
  executeChangeEvents(
    node: BuilderNode,
    newValue: any,
    oldValue: any,
    formData: Record<string, any>
  ): void {
    const events = this.getEventsForType(node, 'onChange');
    
    if (events.length === 0) {
      return;
    }

    console.log(`[EventScript] Executando ${events.length} evento(s) onChange para componente:`, node.id);

    events.forEach(event => {
      const context = this.buildContext(node, formData, newValue, oldValue);
      this.runtimeService.execute(event, context);
    });
  }

  /**
   * Obtém eventos de um tipo específico para um node
   */
  private getEventsForType(node: BuilderNode, eventType: 'onChange' | 'onInit'): EventConfig[] {
    const events = (node.props as any).events as EventConfig[] | undefined;
    
    if (!events || !Array.isArray(events)) {
      return [];
    }

    return events.filter(event => 
      event.eventType === eventType && 
      event.enabled === true
    );
  }

  /**
   * Constrói contexto de execução para um evento
   */
  private buildContext(
    node: BuilderNode,
    formData: Record<string, any>,
    currentValue: any,
    previousValue: any
  ): EventContext {
    return {
      currentValue: currentValue !== null ? currentValue : formData[node.id],
      previousValue: previousValue,
      componentId: node.id,
      formData: { ...formData },
      api: null as any // API será injetada pelo runtime
    };
  }

  /**
   * Valida um evento antes de salvar
   * @param event Evento a ser validado
   * @param availableIds IDs disponíveis no canvas
   */
  validateEvent(event: EventConfig, availableIds: string[]): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validar estrutura básica
    if (!event.eventType) {
      errors.push('Tipo de evento é obrigatório');
    }

    if (!event.mode) {
      errors.push('Modo do evento é obrigatório');
    }

    // Validar modo simples
    if (event.mode === 'simple') {
      if (!event.simpleConditions || event.simpleConditions.length === 0) {
        errors.push('Modo simples requer pelo menos uma condição');
      }

      if (!event.simpleActions || event.simpleActions.length === 0) {
        errors.push('Modo simples requer pelo menos uma ação');
      }

      // Validar referências nas ações
      if (event.simpleActions) {
        event.simpleActions.forEach((action, index) => {
          if (!action.targetId) {
            errors.push(`Ação ${index + 1}: Target ID é obrigatório`);
          } else if (!availableIds.includes(action.targetId)) {
            errors.push(`Ação ${index + 1}: Target ID '${action.targetId}' não encontrado no canvas`);
          }
        });
      }
    }

    // Validar modo expert
    if (event.mode === 'expert') {
      if (!event.customScript || !event.customScript.code) {
        errors.push('Modo expert requer código customizado');
      } else {
        const validationResult = this.validatorService.validate(event.customScript.code);
        if (!validationResult.valid) {
          errors.push(...validationResult.errors);
        }

        // Validar referências no código
        const refValidation = this.validatorService.validateReferences(
          event.customScript.code,
          availableIds
        );
        if (refValidation.warnings && refValidation.warnings.length > 0) {
          // Warnings não impedem salvamento, apenas registram
          console.warn('[EventScript] Warnings de validação:', refValidation.warnings);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Cria um novo evento vazio
   */
  createEmptyEvent(eventType: 'onChange' | 'onInit' = 'onChange'): EventConfig {
    return {
      id: this.generateEventId(),
      eventType,
      mode: 'simple',
      enabled: true,
      description: '',
      simpleConditions: [],
      simpleActions: []
    };
  }

  /**
   * Gera ID único para evento
   */
  private generateEventId(): string {
    return `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clona um evento existente
   */
  cloneEvent(event: EventConfig): EventConfig {
    return {
      ...event,
      id: this.generateEventId(),
      description: `${event.description} (cópia)`
    };
  }

  /**
   * Obtém estatísticas de execução
   */
  getExecutionStats() {
    return this.runtimeService.getExecutionStats();
  }
}



